import React, { useMemo, useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  format,
  parseISO,
  startOfWeek,
  subDays,
  isAfter,
} from "date-fns";
import { fr } from "date-fns/locale";
import {
  countVisitesByDate,
  deleteVisitesByDate,
} from "./../../../../services/visitesServices";

const parseDateSafe = (s) => {
  const v = String(s || "");
  if (!v) return null;
  // si déjà ISO avec T
  if (v.includes("T")) return new Date(v);
  // date simple yyyy-mm-dd
  return new Date(`${v}T00:00:00`);
};

const VisitsChart = () => {
  const [raw, setRaw] = useState([]);
  const [range, setRange] = useState(90); // 30 / 90 / 365 / 0(all)
  const [loading, setLoading] = useState(false);

  // suppression
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [deleting, setDeleting] = useState(false);

  const card =
    "rounded-3xl border border-black/10 bg-white/70 shadow-sm backdrop-blur-sm " +
    "dark:bg-slate-950/35 dark:border-white/10 dark:shadow-none";
  const softText = "text-slate-900 dark:text-white";
  const mutedText = "text-slate-600 dark:text-slate-300";

  const chip =
    "inline-flex items-center justify-center rounded-2xl px-3 py-1.5 text-sm font-extrabold " +
    "border border-black/10 dark:border-white/10 " +
    "bg-white/60 dark:bg-black/20 " +
    "hover:bg-white/80 dark:hover:bg-black/30 transition";

  const input =
    "w-full rounded-2xl px-3 py-2 text-sm outline-none transition " +
    "border border-black/10 dark:border-white/10 " +
    "bg-white/70 dark:bg-black/20 " +
    "text-slate-900 dark:text-white " +
    "focus:ring-2 focus:ring-primary/60 focus:border-transparent";

  const load = async () => {
    try {
      setLoading(true);
      const res = await countVisitesByDate();
      setRaw(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error("Erreur visites:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const rows = (raw || [])
      .map((r) => {
        const dt = parseDateSafe(r.date_visite);
        return {
          dt,
          dateISO: dt ? format(dt, "yyyy-MM-dd") : "",
          compteur: Number(r.compteur || 0),
        };
      })
      .filter((x) => x.dt && x.dateISO)
      .sort((a, b) => a.dt.getTime() - b.dt.getTime());

    if (range === 0) return rows;
    const cutoff = subDays(new Date(), range);
    return rows.filter((x) => isAfter(x.dt, cutoff));
  }, [raw, range]);

  // Agrégation par semaine (moyenne/jour)
  const weekly = useMemo(() => {
    const map = new Map();

    filtered.forEach((r) => {
      const wk = startOfWeek(r.dt, { weekStartsOn: 1 });
      const key = format(wk, "yyyy-MM-dd");
      if (!map.has(key)) map.set(key, { weekStart: wk, sum: 0, days: 0 });
      const entry = map.get(key);
      entry.sum += r.compteur;
      entry.days += 1;
    });

    const arr = Array.from(map.values())
      .sort((a, b) => a.weekStart.getTime() - b.weekStart.getTime())
      .map((w) => ({
        weekLabel: format(w.weekStart, "dd/MM", { locale: fr }),
        avgPerDay: w.days ? Number((w.sum / w.days).toFixed(2)) : 0,
        total: w.sum,
      }));

    return arr;
  }, [filtered]);

  const kpis = useMemo(() => {
    const total = filtered.reduce((acc, r) => acc + r.compteur, 0);
    const daysCount = filtered.length || 1;
    const avg = total / daysCount;

    const bestWeek = weekly.reduce(
      (best, w) => (w.avgPerDay > (best?.avgPerDay ?? -1) ? w : best),
      null
    );

    return {
      total,
      avgPerDay: Number(avg.toFixed(2)),
      bestWeekLabel: bestWeek ? bestWeek.weekLabel : "—",
      bestWeekAvg: bestWeek ? bestWeek.avgPerDay : 0,
    };
  }, [filtered, weekly]);

  const handleDelete = async () => {
    if (!dateDebut || !dateFin) return;
    const ok = window.confirm(
      `Confirmer la suppression des visites du ${dateDebut} au ${dateFin} ?`
    );
    if (!ok) return;

    try {
      setDeleting(true);
      const response = await deleteVisitesByDate(dateDebut, dateFin);
      alert(response?.message || "Visites supprimées avec succès");
      await load();
    } catch (e) {
      console.error("Erreur suppression visites:", e);
      alert("Une erreur est survenue lors de la suppression des visites.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={`${card} p-4 sm:p-5`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className={`text-base sm:text-lg font-extrabold ${softText}`}>
            Visites du site (moyennes)
          </h3>
          <p className={`text-xs ${mutedText}`}>
            Affichage par semaine (moyenne / jour) pour éviter une liste de dates trop dense.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" className={chip} onClick={() => setRange(30)}>
            30 j
          </button>
          <button type="button" className={chip} onClick={() => setRange(90)}>
            90 j
          </button>
          <button type="button" className={chip} onClick={() => setRange(365)}>
            1 an
          </button>
          <button type="button" className={chip} onClick={() => setRange(0)}>
            Tout
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 p-4">
          <div className={`text-xs font-bold ${mutedText}`}>Total période</div>
          <div className={`mt-1 text-2xl font-extrabold ${softText}`}>{kpis.total}</div>
        </div>

        <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 p-4">
          <div className={`text-xs font-bold ${mutedText}`}>Moyenne / jour</div>
          <div className={`mt-1 text-2xl font-extrabold ${softText}`}>{kpis.avgPerDay}</div>
        </div>

        <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 p-4">
          <div className={`text-xs font-bold ${mutedText}`}>Meilleure semaine</div>
          <div className={`mt-1 text-lg font-extrabold ${softText}`}>
            {kpis.bestWeekLabel}
            <span className={`ml-2 text-xs font-semibold ${mutedText}`}>
              ({kpis.bestWeekAvg}/j)
            </span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="mt-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 p-3">
        {loading ? (
          <p className={`text-center text-sm ${mutedText}`}>Chargement…</p>
        ) : (
          <div style={{ position: "relative", zIndex: 0 }}>
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={weekly}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="weekLabel" />
                <YAxis />
                <Tooltip
                  formatter={(v) => [`${v}`, "Moyenne / jour"]}
                  labelFormatter={(lbl) => `Semaine du ${lbl}`}
                  contentStyle={{
                    backgroundColor: "rgba(15, 23, 42, 0.92)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#fff",
                    borderRadius: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="avgPerDay"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Delete (advanced) */}
      <details className="mt-4">
        <summary className={`cursor-pointer text-sm font-bold ${softText}`}>
          Actions avancées
        </summary>

        <div className="mt-3 rounded-2xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 p-4">
          <h4 className={`text-sm font-extrabold ${softText}`}>
            Supprimer des visites entre deux dates
          </h4>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="date"
              className={input}
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
            />
            <input
              type="date"
              className={input}
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
            />
            <button
              type="button"
              className={`
                rounded-2xl px-4 py-2 font-extrabold text-white transition
                ${deleting ? "bg-slate-500" : "bg-red-600 hover:bg-red-700"}
              `}
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Suppression…" : "Supprimer"}
            </button>
          </div>
        </div>
      </details>
    </div>
  );
};

export default VisitsChart;
